import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Alert,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import ProtectedSection from '../components/ProtectedSection';
import transactionService from '../services/transactionService';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

export default function TransactionPage() {
  const initialDates = useMemo(() => defaultDateRange(), []);
  const [txs, setTxs] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const tiKaneId = params.get('tiKaneId');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchRows = useCallback(() => {
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) {
      setErrorMsg(dateError); setTxs([]); setTotal(0); return;
    }
    setErrorMsg('');
    transactionService.list({
      tiKaneId: tiKaneId || undefined,
      page, pageSize, search, type: typeFilter, sortDir, dateFrom, dateTo,
    }).then((res) => {
      setTxs(res?.data || []);
      setTotal(Number(res?.total || 0));
      const pages = Math.max(1, Number(res?.totalPages || Math.ceil(Number(res?.total || 0) / pageSize)));
      if (page > pages) setPage(pages);
    }).catch((err) => {
      if (err.response?.status === 403) navigate('/login');
      else setErrorMsg(err?.response?.data?.message || 'Erreur lors du chargement des transactions.');
    });
  }, [dateFrom, dateTo, navigate, page, pageSize, search, sortDir, tiKaneId, typeFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [search, typeFilter, sortDir, pageSize, tiKaneId, dateFrom, dateTo]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) {
      items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    }
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const firstItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem = Math.min(page * pageSize, total);

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{tiKaneId ? `Transactions du compte #${tiKaneId}` : 'Toutes les transactions'}</h2>
      </div>
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <Row className="g-2 align-items-end mb-3">
        <Col md={3}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
        <Col md={3}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
        <Col md={6}>
          <Form.Label>Recherche</Form.Label>
          <InputGroup><Form.Control placeholder="Compte, code, type, détails..." value={search} onChange={(e) => setSearch(e.target.value)} /><Button variant="outline-secondary" onClick={() => setSearch('')}>×</Button></InputGroup>
        </Col>
        <Col md={3}><Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tous types</option><option value="CREDIT">Dépôt</option><option value="DEBIT">Retrait</option></Form.Select></Col>
        <Col md={3}><Form.Select value={sortDir} onChange={(e) => setSortDir(e.target.value)}><option value="desc">Plus récent → plus ancien</option><option value="asc">Plus ancien → plus récent</option></Form.Select></Col>
        <Col md={3}><Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 / page</option><option value={20}>20 / page</option><option value={50}>50 / page</option><option value={100}>100 / page</option></Form.Select></Col>
      </Row>

      <ProtectedSection code="TRANS_VIEW">
        <Table hover responsive>
          <thead><tr><th>ID</th><th>Compte</th><th>Type</th><th>Montant</th><th>Solde après</th><th>Date</th></tr></thead>
          <tbody>
            {txs.map((tx) => <tr key={tx.id}>
              <td>{tx.id}</td><td>{tx.TiKane?.accountNumber ?? tx.tiKaneId}</td><td>{tx.type}</td>
              <td>{Number(tx.amount).toFixed(2)}</td><td>{Number(tx.balanceAfter).toFixed(2)}</td><td>{new Date(tx.createdAt).toLocaleString()}</td>
            </tr>)}
            {txs.length === 0 && <tr><td colSpan={6} className="text-center text-muted">Aucune transaction trouvée.</td></tr>}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="text-muted">Résultats : {firstItem}–{lastItem} sur {total}</small>
          <Pagination className="m-0">{paginationItems}</Pagination>
        </div>
      </ProtectedSection>
    </Container>
  );
}
